import React, { useEffect, useState } from "react";
import { Panel, Field } from "@/pages/admin/shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/pages/admin/api";

export default function InvoiceSettings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings/invoice").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const set = (k, v) => setData({ ...data, [k]: v });

  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/settings/invoice", data); setData(r.data); toast.success("Invoice settings updated"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Panel title="Invoice Settings" actions={<Button data-testid="save-invoice-settings" onClick={save} disabled={saving} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white"><Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save"}</Button>}>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Invoice Prefix"><Input value={data.prefix || ""} onChange={(e) => set("prefix", e.target.value)} placeholder="INV-" /></Field>
        <Field label="Starting Invoice Number"><Input type="number" value={data.start_number || 1} onChange={(e) => set("start_number", Number(e.target.value))} /></Field>
        <Field label="Default Currency"><Input value={data.default_currency || ""} onChange={(e) => set("default_currency", e.target.value)} placeholder="USD" /></Field>
        <Field label="Default Tax (%)"><Input type="number" step="0.01" value={data.default_tax || 0} onChange={(e) => set("default_tax", Number(e.target.value))} /></Field>
        <Field label="Default Discount (%)"><Input type="number" step="0.01" value={data.default_discount || 0} onChange={(e) => set("default_discount", Number(e.target.value))} /></Field>
        <Field label="Default Shipping"><Input type="number" step="0.01" value={data.default_shipping || 0} onChange={(e) => set("default_shipping", Number(e.target.value))} /></Field>
        <Field label="Company Logo URL" className="md:col-span-2"><Input value={data.company_logo || ""} onChange={(e) => set("company_logo", e.target.value)} placeholder="https://... or paste base64 data URL" /></Field>
        <Field label="Invoice Footer Text" className="md:col-span-2"><Textarea rows={2} value={data.footer_text || ""} onChange={(e) => set("footer_text", e.target.value)} /></Field>
        <Field label="Default Notes"><Textarea rows={3} value={data.default_notes || ""} onChange={(e) => set("default_notes", e.target.value)} /></Field>
        <Field label="Default Terms & Conditions"><Textarea rows={3} value={data.default_terms || ""} onChange={(e) => set("default_terms", e.target.value)} /></Field>
      </div>

      <div className="rounded-xl border border-border p-4 mt-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Show bottom branding text on invoice</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              When off, the line below is removed from the bottom of every invoice preview and PDF.
            </p>
          </div>
          <Switch
            data-testid="invoice-branding-toggle"
            checked={data.show_footer_branding !== false}
            onCheckedChange={(v) => set("show_footer_branding", v)}
          />
        </div>
        <Field label="Bottom branding text" hint="Shown centered at the very bottom of the invoice, below signature.">
          <Input
            data-testid="invoice-branding-text-input"
            value={data.footer_branding_text || ""}
            onChange={(e) => set("footer_branding_text", e.target.value)}
            placeholder="Thank you for your business · Generated with Simple Invoice"
            disabled={data.show_footer_branding === false}
          />
        </Field>
      </div>
    </Panel>
  );
}
